import React from 'react';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '@/shared/components/ui/button/Button';
import { Form, Input, Label } from '@/shared/components/form';

type MfaSetupProps = {
    moveNext: () => void;
};

type MfaEmailFormInputs = {
    email: string;
};

export const MfaSetupEmailForm: React.FC<MfaSetupProps> = ({ moveNext }) => {
    const { setupMFA, user } = useAuth();

    const { control, handleSubmit, setError } = useForm<MfaEmailFormInputs>({
        defaultValues: {
            email: user?.email || '',
        },
        mode: 'onBlur',
        reValidateMode: 'onChange',
    });

    const onSubmit: SubmitHandler<MfaEmailFormInputs> = async (data) => {
        // This function is not used in this component, but it can be used for form validation if needed.
        // Currently, we are directly calling getEmailCode on button click.
        try {
            await setupMFA({ ...data, method: 'email' });
            moveNext();
        } catch (error) {
            setError('email', {
                type: 'server',
                message: error instanceof Error ? error.message : 'Failed to send MFA code',
            });
        }
    };

    return (
        <>
            <div className="flex flex-row">
                <Form onSubmit={handleSubmit(onSubmit)} className="flex-grow p-4 space-y-4">
                    <p className="mt-2 text-sm text-mp-muted">
                        Please verify the email address you want to use for MFA
                    </p>
                    <Label htmlFor="email">
                        Email <span className="text-mp-error">*</span>{' '}
                    </Label>
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: 'Email is required',
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Enter a valid email address',
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <Input
                                type="email"
                                id="email"
                                placeholder="Enter your email address"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                error={!!fieldState.error}
                                hint={fieldState.error?.message}
                            />
                        )}
                    />
                    <div className="flex flex-row justify-end">
                        <div className="mt-4 w-full lg:w-1/3">
                            <Button className="w-full">Send</Button>
                        </div>
                    </div>
                </Form>
            </div>
        </>
    );
};
